import {
  IMPACT_GROUP_NAME_MAX_LENGTH,
  ImpactGroup,
  InputVariantTypes,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import { useState } from "react";
import { Select, SelectEvent, SelectItemType } from "../Select";
import { TextField, TextFieldChangeEvent } from "../TextField";

const NEW_GROUP_VALUE = "__new__";

const GroupSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export type GroupSelectorProps = {
  groupId: number | null;
  groups: ImpactGroup[];
  inlineDropdown?: boolean;
  isDisabled?: boolean;
  isLoggedIn: boolean;
  onCreateGroup: (name: string) => Promise<ImpactGroup>;
  onGroupChange: (groupId: number | null) => void;
  showHelpText?: boolean;
  showLabel?: boolean;
  variant?: InputVariantTypes;
  width?: string;
};

export const GroupSelector = ({
  groupId,
  groups,
  inlineDropdown,
  isDisabled = false,
  isLoggedIn,
  onCreateGroup,
  onGroupChange,
  showHelpText = true,
  showLabel = true,
  variant = InputVariantTypes.DEFAULT,
  width,
}: GroupSelectorProps) => {
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const handleGroupSelectChange = (event: SelectEvent) => {
    const value = event.target.value;

    if (value === NEW_GROUP_VALUE) {
      setShowNewGroupInput(true);
      return;
    }

    if (value === "") {
      onGroupChange(null);
      setShowNewGroupInput(false);
      return;
    }

    const selectedGroup = groups.find(
      (g): g is ImpactGroup & { id: number } =>
        g.id !== undefined && g.id.toString() === value
    );
    if (selectedGroup) {
      onGroupChange(selectedGroup.id);
      setShowNewGroupInput(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || isCreatingGroup) return;

    try {
      setIsCreatingGroup(true);
      const createdGroup = await onCreateGroup(newGroupName.trim());
      if (createdGroup.id) {
        onGroupChange(createdGroup.id);
        setNewGroupName("");
        setShowNewGroupInput(false);
      }
    } catch (error) {
      console.error("Failed to create group:", error);
      // Keep input visible on error so user can retry
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleNewGroupInputChange = (e: TextFieldChangeEvent) => {
    setNewGroupName(e.target.value);
  };

  const groupSelectItems: SelectItemType[] = [
    { label: "Select a group", value: "" },
    ...groups
      .filter((group): group is ImpactGroup & { id: number; name: string } =>
        Boolean(group.id && group.name)
      )
      .map((group) => ({
        label: group.name,
        value: group.id.toString(),
      })),
    { label: "+ Add a new group", value: NEW_GROUP_VALUE },
  ];

  const currentGroupValue = showNewGroupInput
    ? NEW_GROUP_VALUE
    : groupId
    ? groupId.toString()
    : "";

  return (
    <GroupSelectorContainer>
      <Select
        data-testid="group-select"
        helpText={
          isLoggedIn
            ? showHelpText
              ? "Assign impact to a group or project"
              : undefined
            : "Only available when logged in"
        }
        inlineDropdown={inlineDropdown}
        label="Group (optional)"
        hiddenLabel={!showLabel}
        isDisabled={isDisabled || !isLoggedIn}
        name="group"
        items={groupSelectItems}
        value={currentGroupValue}
        onChange={handleGroupSelectChange}
        variant={variant}
        width={width}
      />
      {showNewGroupInput && (
        <TextField
          data-testid="new-group-input"
          helpText={
            showHelpText ? "Enter a name for your new group" : undefined
          }
          label="New group name"
          isDisabled={isDisabled || isCreatingGroup}
          name="newGroupName"
          placeholder="e.g., Marketing team"
          onChange={handleNewGroupInputChange}
          onBlur={handleCreateGroup}
          maxLength={IMPACT_GROUP_NAME_MAX_LENGTH}
          showCharacterCount={newGroupName.length > 0}
          variant={variant}
          value={newGroupName}
        />
      )}
    </GroupSelectorContainer>
  );
};
